import { activityFactory } from 'factories/activities';
import type {
  ActivityClientNormalized,
  ActivityProjectNormalized,
} from 'redux/modules/activities.types';
import { RegularActivityTypes } from 'redux/modules/activities.types';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import {
  PROJECT_ACTIVITY_SCHEMA_KEY,
  USER_ACTIVITY_SCHEMA_KEY,
  USERS_SCHEMA_KEY,
} from 'schema/activities';
import { getProjectVariantMap } from './projects';
import { getCurrentlyViewedProjectId } from './projects.shared';
import { getUserTimeFormat } from './user';
import { extractProjectBase } from './utils/projects';

const readXhrStatus = (state: IState) => state.activities.xhrStatus;
const readActivityEntities = (state: IState) => state.activities.entities;
const readActivityResult = (state: IState) => state.activities.result;
const readActivityMaybeHasMore = (state: IState) => state.activities.maybeHasMore;

export const getActivityMaybeHasMore = readActivityMaybeHasMore;
export const getActivityEntities = readActivityEntities;
export const getActivityResult = readActivityResult;

export const getActivityXhrStatus = createSelector([readXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);

export const getAllUserActivities = createSelector(
  [getActivityEntities, getActivityResult, getUserTimeFormat],
  (activityEntities, activityResult, timeFormat) => {
    const entities = activityEntities[USER_ACTIVITY_SCHEMA_KEY];
    const result = activityResult[USER_ACTIVITY_SCHEMA_KEY];

    return result.map(k => {
      const activity = entities[k];
      const user = activityEntities[USERS_SCHEMA_KEY][activity.user];
      return activityFactory({
        activity,
        user,
        project:
          activity.entity_type === 'Project'
            ? (activityEntities.entityContext[activity.entity] as ActivityProjectNormalized)
            : undefined,
        client:
          activity.entity_type === 'Company'
            ? (activityEntities.entityContext[activity.entity] as ActivityClientNormalized)
            : undefined,
        timeFormat,
      });
    });
  }
);

export const getProjectActivities = createSelector(
  [
    readActivityEntities,
    readActivityResult,
    getProjectVariantMap,
    getCurrentlyViewedProjectId,
    getUserTimeFormat,
  ],
  (activityEntities, activityResult, projectMap, currentProjectId, timeFormat) => {
    const entities = activityEntities[PROJECT_ACTIVITY_SCHEMA_KEY];
    const result = activityResult[PROJECT_ACTIVITY_SCHEMA_KEY];

    return result
      .filter(currentActivityId => !!entities[currentActivityId])
      .filter(currentActivityId => {
        // if we're not on projects/:id, don't scope activities:
        if (!currentProjectId) {
          return true;
        }

        const activity = entities[currentActivityId];

        // Is the activity for the project id?
        const isForProject = activity.entity === currentProjectId;
        // ...or for the project's client?
        const variant = projectMap[currentProjectId];
        const isForProjectClient =
          activity.activity_type === RegularActivityTypes.archive_client &&
          !!variant &&
          activity.entity === extractProjectBase(variant).client.id;

        return isForProject || isForProjectClient;
      })
      .map(key => {
        const activity = entities[key];
        // Reading API's embedded user/project here, but might want to
        // read from project/users store slice instead - post normalization, hopefully
        const user = activityEntities[USERS_SCHEMA_KEY][activity.user];
        return activityFactory({
          activity,
          user,
          project:
            activity.entity_type === 'Project'
              ? (activityEntities.entityContext[activity.entity] as ActivityProjectNormalized)
              : undefined,
          client:
            activity.entity_type === 'Company'
              ? (activityEntities.entityContext[activity.entity] as ActivityClientNormalized)
              : undefined,
          timeFormat,
        });
      });
  }
);
